import { useCallback } from 'react';
import { useMilestoneContext } from '@/contexts/MilestoneContext';
import type { MilestoneType } from '@/components/milestones/MilestoneCelebration';

// Re-export the main hook from context
export function useMilestone() {
  return useMilestoneContext();
}

// Helper hooks for specific milestone tracking
export function useFirstSmsMilestone() {
  const { achieveMilestone, hasMilestone } = useMilestoneContext();

  const trackFirstSms = useCallback(async (messageId: string) => {
    if (!hasMilestone('first_sms_sent')) {
      await achieveMilestone({
        milestoneType: 'first_sms_sent',
        metadata: { contextId: messageId, contextType: 'message' }
      });
    }
  }, [achieveMilestone, hasMilestone]);

  return { trackFirstSms, hasAchieved: hasMilestone('first_sms_sent') };
}

export function useFirstCallMilestone() {
  const { achieveMilestone, hasMilestone } = useMilestoneContext();

  const trackFirstCall = useCallback(async (callId: string) => {
    if (!hasMilestone('first_call_made')) {
      await achieveMilestone({
        milestoneType: 'first_call_made',
        metadata: { contextId: callId, contextType: 'call' }
      });
    }
  }, [achieveMilestone, hasMilestone]);

  return { trackFirstCall, hasAchieved: hasMilestone('first_call_made') };
}

export function useContactsMilestone() {
  const { achieveMilestone, hasMilestone } = useMilestoneContext();

  const trackContactsAdded = useCallback(async (currentCount: number) => {
    if (!hasMilestone('contacts_10_added') && currentCount >= 10) {
      await achieveMilestone({
        milestoneType: 'contacts_10_added',
        metadata: { count: currentCount, contextType: 'contacts' }
      });
    }
  }, [achieveMilestone, hasMilestone]);

  return { trackContactsAdded, hasAchieved: hasMilestone('contacts_10_added') };
}

export function useAiComposerMilestone() {
  const { achieveMilestone, hasMilestone } = useMilestoneContext();

  const trackAiComposerUsed = useCallback(async (messageId?: string) => {
    if (!hasMilestone('ai_composer_used')) {
      await achieveMilestone({
        milestoneType: 'ai_composer_used',
        metadata: { contextId: messageId, contextType: 'ai_message' }
      });
    }
  }, [achieveMilestone, hasMilestone]);

  return { trackAiComposerUsed, hasAchieved: hasMilestone('ai_composer_used') };
}
